<template>
  <div class="pt-4 pb-20 px-4">
    <h3 class="text-2xl font-bold mb-5">
      {{ account ? `${appTypeTitle[account.app_type]} - ${account.title}` : '' }}
    </h3>

    <div class="block">
      <nav class="flex space-x-4 mb-5" aria-label="Tabs">
        <a
            v-for="tab in tabs"
            :key="tab.key"
            href="javascript:void(0)"
            :class="[
              tab.key === activeTab 
                ? `${$theme.activeTabBg} ${$theme.activeTabBorder} ${$theme.activeTabTextColor}` 
                : `${$theme.tabTextColor} border-transparent opacity-70`, 
                'rounded-lg px-3 py-2 border text-sm font-medium'
            ]"
            @click="switchTab(tab.key)"
        >
          {{ tab.name }}
          <span v-if="positions.length && tab.key === 'positions'" class="ml-1">({{ positions.length }})</span>
        </a>
      </nav>
    </div>

    <template v-if="loading">
      <div class="rounded-xl" :class="[$theme.cardBg]">
        <LoadingBlock />
      </div>
    </template>

    <template v-else>
      <template v-if="activeTab === 'account'">
        <!-- Основная информация -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div class="rounded-lg border p-3" :class="[$theme.cardBg, $theme.cardBorderColor]">
            <h4 class="text-lg font-semibold mb-4">Account Credentials</h4>
            <div class="space-y-2">
              <div>
                <div class="text-xs opacity-50">Client ID</div>
                <div class="font-mono flex items-center">
                  {{ account.credentials.client_id }}
                  <button
                      @click="copyToClipboard(account.credentials.client_id)"
                      class="ml-1 hover:opacity-60 transition-opacity"
                  >
                    <ClipboardDocumentIcon class="w-5 h-5 opacity-30 cursor-pointer" />
                  </button>
                </div>
              </div>
              <div>
                <div class="text-xs opacity-50">Secret Key</div>
                <div class="font-mono flex items-center">
                  {{ account.credentials.secret_key }}
                  <button
                      @click="copyToClipboard(account.credentials.secret_key)"
                      class="ml-1 hover:opacity-60 transition-opacity"
                  >
                    <ClipboardDocumentIcon class="w-5 h-5 opacity-30 cursor-pointer" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Торговые параметры -->
          <div class="rounded-lg border p-3" :class="[$theme.cardBg, $theme.cardBorderColor]">
            <h4 class="text-lg font-semibold mb-4">Trading Parameters</h4>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <div class="text-xs opacity-50">Leverage</div>
                <div>1:{{ account.leverage }}</div>
              </div>
              <div>
                <div class="text-xs opacity-50">Currency</div>
                <div>{{ account.currency }}</div>
              </div>
              <div>
                <div class="text-xs opacity-50">Margin Mode</div>
                <div>{{ marginModes[account.margin_mode] }}</div>
              </div>
              <div>
                <div class="text-xs opacity-50">Trade Mode</div>
                <div>{{ tradeModes[account.trade_mode] }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Финансовые показатели -->
        <div class="rounded-lg border p-3 mb-6" :class="[$theme.cardBg, $theme.cardBorderColor]">
          <h4 class="text-lg font-semibold mb-4">Financial Summary</h4>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div class="text-xs opacity-50">Balance</div>
              <div>{{ formatCurrency(account.balance, account.currency) }}</div>
            </div>
            <div>
              <div class="text-xs opacity-50">Equity</div>
              <div>{{ formatCurrency(account.equity, account.currency) }}</div>
            </div>
            <div>
              <div class="text-xs opacity-50">Margin</div>
              <div>{{ formatCurrency(account.margin, account.currency) }}</div>
            </div>
            <div>
              <div class="text-xs opacity-50">Free Margin</div>
              <div>{{ formatCurrency(account.free_margin, account.currency) }}</div>
            </div>
            <div>
              <div class="text-xs opacity-50">Profit</div>
              <div :class="account.profit >= 0 ? 'text-green-500' : 'text-red-500'">
                {{ formatCurrency(account.profit, account.currency) }}
              </div>
            </div>
            <div>
              <div class="text-xs opacity-50">Credit</div>
              <div>{{ formatCurrency(account.credit, account.currency) }}</div>
            </div>
            <div>
              <div class="text-xs opacity-50">Margin Level</div>
              <div>{{ account.margin_level }}%</div>
            </div>
          </div>
        </div>

        <!-- Статусы -->
        <div class="rounded-lg border p-3" :class="[$theme.cardBg, $theme.cardBorderColor]">
          <h4 class="text-lg font-semibold mb-4">Account Status</h4>
          <div class="flex flex-wrap gap-4">
            <div class="flex items-center gap-2">
            <span class="w-2 h-2 rounded-full"
                  :class="account.trade_allowed ? 'bg-green-500' : 'bg-red-500'"></span>
              Trading Allowed: {{ account.trade_allowed ? 'Yes' : 'No' }}
            </div>
            <div class="flex items-center gap-2">
            <span class="w-2 h-2 rounded-full"
                  :class="account.trade_expert ? 'bg-green-500' : 'bg-red-500'"></span>
              Expert Advisor: {{ account.trade_expert ? 'Enabled' : 'Disabled' }}
            </div>
          </div>
        </div>
      </template>

      <template v-if="activeTab === 'positions'">
        <div v-if="historyLoading" class="rounded-xl" :class="[$theme.cardBg]">
          <LoadingBlock />
        </div>

        <div v-else>
          <div class="space-y-3">
            <div v-for="(position, index) in positions" :key="index"
                 class="rounded-lg border p-3" :class="[$theme.cardBg, $theme.cardBorderColor]">
              <div class="flex justify-between items-center mb-2">
                <div class="flex items-center gap-2">
                  <span class="text-lg font-semibold">{{ position.symbol }}</span>
                  <span class="text-sm px-2 py-1 rounded"
                        :class="position.type === 'BUY'
                      ? 'bg-green-500/20 text-green-500'
                      : 'bg-red-500/20 text-red-500'">
                {{ position.type }}
              </span>
                </div>
                <span class="text-sm opacity-70">
              {{ position.orders_count }} orders
            </span>
              </div>

              <div class="grid grid-cols-2 gap-4 text-sm">
                <div class="space-y-1">
                  <div class="flex justify-between">
                    <span class="opacity-70">Total Volume:</span>
                    <span class="font-mono">{{ position.total_volume.toFixed(2) }}</span>
                  </div>
                </div>
                <div class="space-y-1">
                  <div class="flex justify-between">
                    <span class="opacity-70">Type:</span>
                    <span :class="position.type === 'BUY'
                      ? 'text-green-500'
                      : 'text-red-500'">
                  {{ position.type }}
                </span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="!positions.length" class="text-center py-6 opacity-50">
              No open positions
            </div>
          </div>
        </div>
      </template>

      <template v-if="activeTab === 'history'">
        <div v-if="historyLoading" class="rounded-xl" :class="[$theme.cardBg]">
          <LoadingBlock />
        </div>

        <div v-else>
          <div class="space-y-3">
            <div v-for="item in history" :key="item.id"  class="rounded-lg border p-3" :class="[$theme.cardBg, $theme.cardBorderColor]">
              <div class="flex justify-between items-start mb-3">
                <div class="flex-1">
                  <div class="font-medium text-sm">
                    {{ item.action }} • {{ item.symbol }}
                  </div>
                  <div class="text-xs opacity-70">
                    {{ formatDate(item.time) }}
                  </div>
                </div>
                <span class="text-sm" :class="getProfitColor(parseFloat(item.profit))">
            {{ formatCurrency(item.profit, account.currency) }}
          </span>
              </div>

              <div class="grid grid-cols-2 gap-2 text-sm">
                <div class="space-y-1">
                  <div class="flex justify-between">
                    <span class="opacity-70">Volume:</span>
                    <span class="font-mono">{{ item.volume }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="opacity-70">Price:</span>
                    <span class="font-mono">{{ item.price }}</span>
                  </div>
                </div>
                <div class="space-y-1">
                  <div class="flex justify-between">
                    <span class="opacity-70">Commission:</span>
                    <span>{{ formatCurrency(item.commission, account.currency) }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="opacity-70">Swap:</span>
                    <span>{{ formatCurrency(item.swap, account.currency) }}</span>
                  </div>
                </div>
              </div>

              <div v-if="item.comment" class="mt-3 text-xs opacity-70">
                Note: {{ item.comment }}
              </div>
            </div>

            <div v-if="!history.length" class="text-center py-6 opacity-50">
              No history records found
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import LoadingBlock from "@/components/LoadingBlock.vue";
import { ClipboardDocumentIcon } from '@heroicons/vue/20/solid'

export default {
  components: {
    LoadingBlock,
    ClipboardDocumentIcon,
  },

  methods: {
    switchTab(tab) {
      this.activeTab = tab;
      if (tab === 'history' && !this.history.length) {
        this.onLoadHistory();
      }
    },

    onLoadAccount() {
      const id = this.$route.params.id;
      this.loading = true;

      this.$api.get('/forex/accounts/' + id)
          .then((result) => {
            this.account = result.data;
            this.loading = false;

            this.onLoadOpened(this.account.id);
          })
          .catch(e => {
            console.error(e);
            this.loading = false;
          });
    },

    async onLoadHistory() {
      try {
        this.historyLoading = true;
        const { data } = await this.$api.get(`/forex/history`);
        this.history = data.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.historyLoading = false;
      }
    },

    async onLoadOpened(id) {
      try {
        const { data } = await this.$api.get(`/forex/accounts/${id}/opened`);
        console.log(data)

        this.positions = data;
      } catch (e) {
        console.error(e);
      } finally {
        this.historyLoading = false;
      }
    },

    formatCurrency(value, currency) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text)
          .then(() => {
            console.log('Copied to clipboard:', text);
          })
          .catch(err => {
            console.error('Failed to copy text:', err);
          });
    },

    formatDate(timestamp) {
      return new Date(timestamp * 1000).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getProfitColor(profit) {
      return profit >= 0
          ? 'text-green-500'
          : 'text-red-500';
    },
  },

  data() {
    return {
      loading: true,
      account: null,
      history: [],
      positions: [],
      historyLoading: false,
      appTypeTitle: {
        mt4: 'MetaTrader 4',
        mt5: 'MetaTrader 5',
        ctrader: 'cTrader'
      },
      marginModes: {
        0: 'Forex',
        1: 'CFD',
        2: 'Futures'
      },
      tradeModes: {
        0: 'Demo',
        1: 'Real'
      },
      activeTab: 'account',
      tabs: [
        {
          name: 'Account',
          key: 'account',
        },
        {
          name: 'Positions',
          key: 'positions',
        },
        {
          name: 'History',
          key: 'history',
        },
      ],
    }
  },

  mounted() {
    this.onLoadAccount();
    if (this.$route.query.tab === 'history') {
      this.activeTab = 'history';
      this.onLoadHistory();
    }
  }
}
</script>
